import { CommonModule } from '@angular/common';
import { Component, OnInit } from '@angular/core';
import { FormBuilder, FormGroup, ReactiveFormsModule, Validators } from '@angular/forms';
import { ActivatedRoute, Router, RouterModule } from '@angular/router';
import { TranslateModule, TranslateService } from '@ngx-translate/core';
import { NzButtonModule } from 'ng-zorro-antd/button';
import { NzCardModule } from 'ng-zorro-antd/card';
import { NzInputModule } from 'ng-zorro-antd/input';
import { NzMessageService } from 'ng-zorro-antd/message';
import { NzSelectModule } from 'ng-zorro-antd/select';
import {
  PLATFORM_ANNOUNCEMENT_TARGETS,
  PlatformAnnouncementService,
  PlatformAnnouncementTargetKind,
} from '../services/platform-announcement.service';

const MAX_MESSAGE_LENGTH = 500;

@Component({
  selector: 'app-admin-platform-announcement-editor',
  standalone: true,
  imports: [CommonModule, ReactiveFormsModule, RouterModule, TranslateModule, NzButtonModule, NzCardModule, NzInputModule, NzSelectModule],
  templateUrl: './admin-platform-announcement-editor.component.html',
  styleUrl: './admin-platform-announcement-editor.component.less',
})
export class AdminPlatformAnnouncementEditorComponent implements OnInit {
  readonly maxMessageLength = MAX_MESSAGE_LENGTH;
  readonly targetKinds = Object.keys(PLATFORM_ANNOUNCEMENT_TARGETS) as PlatformAnnouncementTargetKind[];
  readonly form: FormGroup;

  announcementId: string | null = null;
  loading = false;
  saving = false;

  constructor(
    private formBuilder: FormBuilder,
    private route: ActivatedRoute,
    private router: Router,
    private service: PlatformAnnouncementService,
    private message: NzMessageService,
    private translate: TranslateService,
  ) {
    this.form = this.formBuilder.group({
      messageDe: ['', [Validators.required, Validators.maxLength(MAX_MESSAGE_LENGTH)]],
      messageEn: ['', [Validators.maxLength(MAX_MESSAGE_LENGTH)]],
      targetKind: [null as PlatformAnnouncementTargetKind | null],
    });
  }

  ngOnInit(): void {
    this.announcementId = this.route.snapshot.paramMap.get('id');
    if (this.announcementId) this.load(this.announcementId);
  }

  get isEditMode(): boolean {
    return !!this.announcementId;
  }

  save(): void {
    if (this.form.invalid) {
      this.form.markAllAsTouched();
      this.message.error(this.translate.instant('ADMIN.PLATFORM_ANNOUNCEMENTS.ERROR_FORM'));
      return;
    }

    this.saving = true;
    const value = this.form.getRawValue();
    const payload = {
      messageDe: value.messageDe?.trim() || '',
      messageEn: value.messageEn?.trim() || null,
      targetKind: value.targetKind || null,
    };
    const request = this.announcementId
      ? this.service.update(this.announcementId, payload)
      : this.service.create(payload);

    request.subscribe({
      next: (announcement) => {
        this.saving = false;
        this.message.success(this.translate.instant('ADMIN.PLATFORM_ANNOUNCEMENTS.SAVED'));
        this.router.navigateByUrl(`/admin/platform-announcements/${announcement._id}`);
      },
      error: () => {
        this.saving = false;
        this.message.error(this.translate.instant('ADMIN.PLATFORM_ANNOUNCEMENTS.ERROR_SAVE'));
      },
    });
  }

  targetLabel(targetKind: PlatformAnnouncementTargetKind): string {
    return this.translate.instant(`ADMIN.PLATFORM_ANNOUNCEMENTS.TARGETS.${targetKind.toUpperCase()}`);
  }

  private load(id: string): void {
    this.loading = true;
    this.service.get(id).subscribe({
      next: (announcement) => {
        this.form.patchValue({
          messageDe: announcement.messageDe,
          messageEn: announcement.messageEn || '',
          targetKind: announcement.targetKind,
        });
        this.form.markAsPristine();
        this.loading = false;
      },
      error: () => {
        this.loading = false;
        this.message.error(this.translate.instant('ADMIN.PLATFORM_ANNOUNCEMENTS.ERROR_LOAD_ONE'));
        this.router.navigateByUrl('/admin/platform-announcements');
      },
    });
  }
}
